/**
 * UI-3a Task 8 — CampaignsPage.
 *
 * Tenant-admin Campaigns list. Composition is plan §0.C (authoritative —
 * supersedes the original spec's row-expansion / per-campaign-pacing / Delete
 * design):
 *
 *   - List columns: Name, Mode, Status, Max CPS, Hours, Lifecycle.
 *   - PacingMonitor lives in the page header showing tenant-wide pacing
 *     (`dialerStore.pacing`) — NOT per-row.
 *   - Lifecycle buttons (Start / Pause / Stop) gated on campaign status.
 *     Stop opens a `confirmDialog` confirmation.
 *   - NO Delete action — campaigns cannot be deleted, so the table is given
 *     `actions={['edit']}` (ResourceTable then omits the Delete button).
 *   - Create / Edit go through `CampaignDrawer` (the drawer owns the save
 *     API call and reports back via `onSaved`).
 *
 * Lifecycle routes (apps/api/src/routes/campaigns/lifecycle.ts):
 *   POST /campaigns/:id/start   -> RUNNING
 *   POST /campaigns/:id/pause   -> PAUSED
 *   POST /campaigns/:id/stop    -> COMPLETED
 */
import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'

import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { CampaignDrawer } from '../../components/admin/campaigns/CampaignDrawer.js'
import { PacingMonitor } from '../../modules/dialer/PacingMonitor.js'
import { useAdminResource } from '../../hooks/useAdminApi.js'
import { api } from '../../api/client.js'
import { useDialerStore, type Campaign } from '../../store/dialerStore.js'
import { useToast } from '../../components/system/ToastProvider.js'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | undefined

/** Maps a campaign status to a PrimeReact Tag severity. */
function statusSeverity(status: Campaign['status']): TagSeverity {
  switch (status) {
    case 'RUNNING':
      return 'success'
    case 'PAUSED':
      return 'warning'
    case 'COMPLETED':
      return 'info'
    case 'DRAFT':
    default:
      return undefined
  }
}

// ---------------------------------------------------------------------------
// Lifecycle buttons (status-gated)
// ---------------------------------------------------------------------------

interface LifecycleHandlers {
  start: (id: string) => void
  pause: (id: string) => void
  stop: (id: string) => void
}

function CampaignLifecycleButtons({
  row,
  on,
}: {
  row: Campaign
  on: LifecycleHandlers
}): JSX.Element {
  return (
    <span className="inline-flex gap-2">
      {(row.status === 'DRAFT' || row.status === 'PAUSED') && (
        <Button
          size="small"
          label="Start"
          severity="success"
          onClick={() => { on.start(row.id) }}
        />
      )}
      {row.status === 'RUNNING' && (
        <Button
          size="small"
          label="Pause"
          severity="warning"
          onClick={() => { on.pause(row.id) }}
        />
      )}
      {row.status !== 'COMPLETED' && (
        <Button
          size="small"
          label="Stop"
          severity="danger"
          outlined
          onClick={() => { on.stop(row.id) }}
        />
      )}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CampaignsPage(): JSX.Element {
  const { items: campaigns, loading, refetch } = useAdminResource<Campaign>(
    '/campaigns',
    { responseKey: 'campaigns', updateMethod: 'PUT' },
  )
  const pacing = useDialerStore((s) => s.pacing)

  const toast = useToast()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Campaign | null>(null)

  async function startCampaign(id: string): Promise<void> {
    try {
      await api.post(`/campaigns/${id}/start`, {})
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start campaign')
    }
  }

  async function pauseCampaign(id: string): Promise<void> {
    try {
      await api.post(`/campaigns/${id}/pause`, {})
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to pause campaign')
    }
  }

  function stopCampaign(id: string): void {
    const c = campaigns.find((x) => x.id === id)
    confirmDialog({
      message: `Stop campaign "${c?.name ?? id}"? Active calls finish but no new attempts. Status becomes COMPLETED.`,
      header: 'Stop Campaign',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        api.post(`/campaigns/${id}/stop`, {}).then(() => {
          refetch()
        }).catch((err: unknown) => {
          toast.error(err instanceof Error ? err.message : 'Failed to stop campaign')
        })
      },
    })
  }

  const lifecycleHandlers: LifecycleHandlers = {
    start: (id) => { void startCampaign(id) },
    pause: (id) => { void pauseCampaign(id) },
    stop: stopCampaign,
  }

  const columns: ColumnDef<Campaign>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'Mode', accessor: (r) => <Tag value={r.mode} severity="info" /> },
    {
      label: 'Status',
      accessor: (r) => <Tag value={r.status} severity={statusSeverity(r.status)} />,
    },
    { label: 'Max CPS', accessor: 'maxCps' },
    {
      label: 'Hours',
      accessor: (r) =>
        r.callingHoursStart && r.callingHoursEnd
          ? `${r.callingHoursStart} – ${r.callingHoursEnd}`
          : '24/7',
    },
    {
      label: 'Lifecycle',
      accessor: (r) => <CampaignLifecycleButtons row={r} on={lifecycleHandlers} />,
    },
  ]

  return (
    <RequirePermission permission="admin:campaigns:view">
      <div className="p-4">
        <PageHeader
          title="Campaigns"
          description="Tenant-wide pacing shown above. Per-campaign pacing in Phase 2."
          actions={
            <>
              <PacingMonitor {...pacing} />
              <Button
                label="+ New Campaign"
                onClick={() => {
                  setEditing(null)
                  setDrawerOpen(true)
                }}
              />
            </>
          }
        />

        <ResourceTable<Campaign>
          columns={columns}
          rows={campaigns}
          loading={loading}
          actions={['edit']}
          onEdit={(r) => {
            setEditing(r)
            setDrawerOpen(true)
          }}
          empty={{
            title: 'No campaigns',
            description: 'Create your first campaign to start dialing.',
            icon: 'pi pi-megaphone',
            action: (
              <Button
                label="+ New Campaign"
                onClick={() => {
                  setEditing(null)
                  setDrawerOpen(true)
                }}
              />
            ),
          }}
        />

        <CampaignDrawer
          open={drawerOpen}
          campaign={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            refetch()
            setDrawerOpen(false)
          }}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
